import React, { useEffect, useMemo } from 'react';
import { Icon, IconifyIcon } from '@iconify/react';
import { TTag } from './types';
import { TBaseTagContext } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { validate } from '@krugom/validator';
import { selectApiState } from '../../../store/selector/selectApiState';
import { selectIcon } from '../../../store/selector/selectIcon';
import { TSiteIcon } from '../../../types';
import { ApiRequest } from '../../../store/saga/api/actions';
import * as config from '../../../config';
import { find, omit } from 'lodash';
import { icons } from '../../../store/saga/initial/constants/icons';

export const CustomIcon: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const validationScheme = useMemo(
    () => ({
      type: 'object',
      properties: {
        icon: {
          type: 'string',
          required: true,
        },
      },
    }),
    []
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const requestKey = `get-icon-${tagProps.icon}`;

  const stIconSelector = useSelector(
    !result.isValid
      ? () => undefined
      : (state: RootState) => selectIcon(state, tagProps.icon) || icons[tagProps.icon]
  );

  const stIcon = useMemo(() => {
    if (!result.isValid) return;
    return stIconSelector;
  }, [result.isValid, stIconSelector]);

  const errIconSelector = useSelector(
    (state: RootState) =>
      selectIcon(state, 'error') || find(state.site.icons, { name: 'default-error' })
  );

  const errIcon = useMemo(() => {
    if (!result.isValid) return;
    return errIconSelector;
  }, [result.isValid, errIconSelector]);

  const pendIconSelector = useSelector(
    (state: RootState) =>
      selectIcon(state, 'pending') || find(state.site.icons, { name: 'default-pending' })
  );

  const pendIcon = useMemo(() => {
    if (!result.isValid) return;
    return pendIconSelector;
  }, [result.isValid, pendIconSelector]);

  const statusSelector = useSelector((state: RootState) => selectApiState(state, requestKey));

  const loadingStatus =
    useMemo(() => {
      if (!result.isValid) return;
      return statusSelector;
    }, [statusSelector, result.isValid])?.status || 'init';

  const dispatch = useDispatch();

  useEffect(() => {
    if (result.isValid && loadingStatus === 'init' && !stIcon) {
      dispatch(
        ApiRequest({
          path: `${config.api.iconsPath}`,
          method: 'GET',
          key: requestKey,
          query: {
            filters: {
              name: tagProps.icon,
            },
          },
        })
      );
    }
  }, [dispatch, loadingStatus, tagProps.icon, result.isValid, stIcon]);

  const icon = stIcon || ((loadingStatus === 'progress' ? pendIcon : errIcon) as TSiteIcon);

  if (!icon) return null;

  const iconProps = {
    ...omit(tagProps, ['children']),
    icon: {
      body: `<path fill="currentcolor" d="${icon.path}" />`,
      width: icon.width,
      height: icon.height,
    } as IconifyIcon,
  };

  if (!result.isValid) {
    console.error('Тэг: $Icon;', `Путь: ${context.path?.join('.') || 'Не указан'};`, result.errors);
    return null;
  }

  return <Icon {...iconProps} />;
});
CustomIcon.displayName = 'CustomIcon';
